import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FaSpinner, FaTimes } from "react-icons/fa"; // Ajout de FaTimes pour l'icône croix

function ImportMappingModal({ columns, modelFields, onConfirm, onCancel, onTest, errors, onDownloadTemplate, loading }) {
    const { t } = useTranslation();
    const [mapping, setMapping] = useState({});

    const fieldOptions = modelFields.map(f => ({
        value: f.name || f,
        label: f.string ? `${f.string} (${f.name})` : f
    }));

    // NOUVEAU : Auto-mapping intelligent au chargement
    useEffect(() => {
        const newMapping = {};
        const normalize = (str) => str ? str.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : '';
        
        columns.forEach(col => {
            const colNorm = normalize(col);
            if (!colNorm) return;

            // 1. Chercher une correspondance exacte (Prioritaire)
            let match = modelFields.find(f => {
                const fName = f.name || f;
                const fLabel = f.string || fName;
                return normalize(fName) === colNorm || normalize(fLabel) === colNorm;
            });
            
            // 2. Si pas trouvé, approche "Like" (contient ou est contenu) pour gérer les groupes de mots
            if (!match) {
                match = modelFields.find(f => {
                    const fName = normalize(f.name || f);
                    const fLabel = normalize(f.string || (f.name || f));

                    if (!fName && !fLabel) return false;

                    // On vérifie si les chaines se contiennent mutuellement (ex: "Nom Employé" <-> "nom")
                    const nameMatch = fName && (colNorm.includes(fName) || fName.includes(colNorm));
                    const labelMatch = fLabel && (colNorm.includes(fLabel) || fLabel.includes(colNorm));
                    
                    return nameMatch || labelMatch;
                });
            }

            if (match) {
                newMapping[col] = match.name || match;
            }
        });
        setMapping(newMapping);
    }, [columns, modelFields]);
    // FIN NOUVEAU

    const handleChange = (col, selectedOption) => {
        setMapping({ ...mapping, [col]: selectedOption ? selectedOption.value : "" });
    };

    // Sécurisation de la fermeture du modal
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onCancel();
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={handleBackdropClick} // Utilisation de la fonction sécurisée
        >
            <div className="bg-white rounded-2xl shadow-2xl p-8 min-w-[600px] flex flex-col gap-6 border border-gray-200">
                {errors && (
                    <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-2 rounded mb-2 max-h-40 overflow-y-auto text-sm">
                        {Array.isArray(errors) ? (
                            <ul className="list-disc pl-5">
                                {errors.map((err, idx) => {
                                    if (typeof err === "string") {
                                        return <li key={idx}>{err}</li>;
                                    }
                                    if (typeof err === "object" && err !== null) {
                                        const lineNum = err.line || err.row;
                                        const linePrefix = lineNum ? (t('importMappingModal.rowPrefix', { row: lineNum }) || `Ligne ${lineNum} : `) : "";

                                        if (err.errors && typeof err.errors === "object") {
                                            return (
                                                <li key={idx}>
                                                    {linePrefix}
                                                    {Object.entries(err.errors).map(([field, msg]) => (
                                                        <span key={field}><b>{field}</b>: {typeof msg === "object" ? JSON.stringify(msg) : msg} </span>
                                                    ))}
                                                </li>
                                            );
                                        }
                                        if (err.error) {
                                            return <li key={idx}>{linePrefix}{typeof err.error === "object" ? JSON.stringify(err.error) : err.error}</li>;
                                        }
                                        if (err.message) {
                                            return <li key={idx}>{linePrefix}{typeof err.message === "object" ? JSON.stringify(err.message) : err.message}</li>;
                                        }
                                        return <li key={idx}>{linePrefix}{JSON.stringify(err)}</li>;
                                    }
                                    return <li key={idx}>{String(err)}</li>;
                                })}
                            </ul>
                        ) : typeof errors === "object" && errors !== null ? (
                            <span>{errors.message || errors.error || JSON.stringify(errors)}</span>
                        ) : (
                            <span>{String(errors)}</span>
                        )}
                    </div>
                )}
                
                <button
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded shadow flex items-center justify-center gap-2"
                        type="button"
                        onClick={onDownloadTemplate}
                    >
                        <span>📥</span> {t('importMappingModal.downloadBlankTemplate')}
                </button>

                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-gray-800">{t('importMappingModal.mapColumns')}</h2>
                    <button
                        className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                        onClick={onCancel}
                        title={t('customReportManager.close')}
                    >
                        ×
                    </button>
                </div>

                <div className="flex flex-col gap-4 max-h-[50vh] overflow-y-auto">
                    <div className="flex gap-3 items-center pb-2 border-b border-gray-200">
                        <span className="w-40 font-semibold text-gray-500 uppercase text-xs tracking-wider">
                            {t('importMappingModal.fileColumn')}
                        </span>
                        <span className="flex-1 font-semibold text-gray-500 uppercase text-xs tracking-wider">
                            {t('importMappingModal.modelField')}
                        </span>
                    </div>
                    {columns.map(col => (
                        <div key={col} className="flex gap-3 items-center">
                            <span className="w-40 font-medium text-gray-700">{col}</span>
                            <div className="flex-1 flex items-center gap-2">
                                <div className="flex-grow">
                                    <Select
                                        options={fieldOptions}
                                        value={fieldOptions.find(opt => opt.value === mapping[col]) || null}
                                        onChange={option => handleChange(col, option)}
                                        isClearable={false} // Désactivé ici pour éviter les bugs, on utilise le bouton dédié à côté
                                        placeholder={t('importMappingModal.fieldPlaceholder')}
                                        classNamePrefix="react-select"
                                    />
                                </div>
                                {/* Bouton manuel pour vider le champ */}
                                <button
                                    onClick={() => handleChange(col, null)}
                                    className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${
                                        mapping[col] ? "text-red-400 hover:text-red-600 cursor-pointer" : "text-gray-200 cursor-default"
                                    }`}
                                    title={t('importMappingModal.clearSelection')}
                                    type="button"
                                    disabled={!mapping[col]}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex gap-3 mt-6 justify-between">
                    <button
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded shadow"
                        onClick={onCancel}
                        type="button"
                        disabled={loading}
                    >
                        {t('basePrimeForm.cancel')}
                    </button>

                    <div className="flex gap-3">
                        <button
                            className="bg-[--color-orange] text-white px-4 py-2 rounded shadow flex items-center gap-2"
                            onClick={() => onTest && onTest(mapping)}
                            type="button"
                            disabled={loading}
                        >
                            {loading ? <FaSpinner className="animate-spin" /> : null}
                            {t('importMappingModal.test')}
                        </button>
                        <button
                            className="bg-[--color-primary]  text-white px-4 py-2 rounded shadow flex items-center gap-2"
                            onClick={() => onConfirm(mapping)}
                            type="button"
                            disabled={loading}
                        >
                            {loading ? <FaSpinner className="animate-spin" /> : null}
                            {t('importMappingModal.importAction')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImportMappingModal;